package or.garden.manager.features.admin

import androidx.compose.runtime.*
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.browser.window
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.PlantingService
import or.garden.manager.domain.model.PlantingItem
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminPlantingMonthScreen(
    monthNumber: Int,
    onBack: () -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit = { _, _ -> }
) {
    val months = listOf(
        "Januar", "Februar", "Mart", "April", "Maj", "Juni",
        "Juli", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
    )
    val monthName = months.getOrElse(monthNumber - 1) { "Nepoznat mjesec" }

    var plantings by remember { mutableStateOf<List<PlantingItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var isGeneratingAi by remember { mutableStateOf(false) }
    var showForm by remember { mutableStateOf(false) }
    var showDeleteAllConfirm by remember { mutableStateOf(false) }
    var editingItem by remember { mutableStateOf<PlantingItem?>(null) }
    var itemToDelete by remember { mutableStateOf<PlantingItem?>(null) }

    fun loadPlantings() {
        isLoading = true
        PlantingService.getPlantings(monthNumber) { state ->
            when (state) {
                is ResultState.Success -> {
                    plantings = state.data.data?.data ?: emptyList()
                    isLoading = false
                }
                is ResultState.Error -> {
                    showSnackbar("Greška pri učitavanju: ${state.message}", SnackbarType.ERROR)
                    isLoading = false
                }
                else -> {}
            }
        }
    }

    LaunchedEffect(monthNumber) {
        loadPlantings()
    }

    fun handleSave() {
        isSaving = true
        PlantingService.savePlantings(monthNumber, plantings) { state ->
            when (state) {
                is ResultState.Success -> {
                    showSnackbar("Uspješno spremljeno!", SnackbarType.SUCCESS)
                    isSaving = false
                }
                is ResultState.Error -> {
                    showSnackbar("Greška pri spremanju: ${state.message}", SnackbarType.ERROR)
                    isSaving = false
                }
                else -> {}
            }
        }
    }

    fun handleDelete(item: PlantingItem) {
        plantings = plantings.filter { it.id != item.id }
        itemToDelete = null
    }

    fun handleDeleteAll() {
        plantings = emptyList()
        showDeleteAllConfirm = false
    }

    fun handleGenerateAi() {
        val lang = "bs" // You could also get current language context
        isGeneratingAi = true
        try {
            PlantingService.generateAiPlantingPlan(monthNumber, lang) { state ->
                when (state) {
                    is ResultState.Success -> {
                        val aiPlantings = state.data.data ?: emptyList()
                        if (aiPlantings.isNotEmpty()) {
                            plantings = plantings + aiPlantings
                            showSnackbar("AI je generisao ${aiPlantings.size} novih stavki!", SnackbarType.SUCCESS)
                        } else {
                            showSnackbar("AI nije vratio nikakve stavke za ovaj mjesec.", SnackbarType.WARNING)
                        }
                        isGeneratingAi = false
                    }
                    is ResultState.Error -> {
                        val errorMessage = state.message ?: "Greška na serveru: AI model trenutno nije dostupan."
                        showSnackbar("Greška: $errorMessage", SnackbarType.ERROR)
                        isGeneratingAi = false
                    }
                    is ResultState.Loading -> {
                        // isGeneratingAi is already true
                    }
                }
            }
        } catch (e: Exception) {
            val errorMsg = e.message ?: "Nepoznata sistemska greška."
            showSnackbar("Sistemska greška: $errorMsg", SnackbarType.ERROR)
            isGeneratingAi = false
        }
    }

    Div({ classes("max-w-4xl", "mx-auto", "pb-20") }) {
        // Header
        Div({ classes("flex", "items-center", "justify-between", "mb-8") }) {
            Div({ classes("flex", "items-center", "gap-4") }) {
                Button({
                    classes("p-2", "text-gray-500", "hover:text-emerald-600", "transition-colors")
                    onClick { onBack() }
                }) {
                    I({ classes("fas", "fa-arrow-left", "text-xl") })
                }
                H1({ classes("text-2xl", "font-bold", "text-gray-900") }) {
                    Text("Plan sadnje: $monthName")
                }
            }

            Div({ classes("flex", "gap-2", "md:gap-3") }) {
                Button({
                    classes("bg-red-50", "text-red-500", "px-3", "py-2", "md:px-4", "rounded-xl", "hover:bg-red-100", "transition-colors", "flex", "items-center", "justify-center")
                    if (plantings.isEmpty()) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick { showDeleteAllConfirm = true }
                }) {
                    I({ classes("fas", "fa-trash-alt") })
                }

                Button({
                    classes("bg-purple-600", "text-white", "px-3", "py-2", "md:px-4", "rounded-xl", "hover:bg-purple-700", "transition-colors", "flex", "items-center", "gap-2")
                    if (isGeneratingAi) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick { handleGenerateAi() }
                }) {
                    if (isGeneratingAi) {
                        I({ classes("fas", "fa-spinner", "fa-spin") })
                    } else {
                        val aiIcon = getCropImageSource("ai")
                        if (aiIcon != null) {
                            Img(src = aiIcon, attrs = { classes("w-5", "h-5", "object-contain", "brightness-0", "invert") })
                        } else {
                            I({ classes("fas", "fa-robot") })
                        }
                    }
                    Span({ classes("hidden", "md:inline") }) { Text("AI Generiši") }
                }
                
                Button({
                    classes("bg-white", "border", "border-gray-200", "text-gray-700", "px-3", "py-2", "md:px-4", "rounded-xl", "hover:bg-gray-50", "transition-colors", "flex", "items-center", "gap-2")
                    onClick {
                        editingItem = null
                        showForm = true
                    }
                }) {
                    I({ classes("fas", "fa-plus") })
                    Span({ classes("hidden", "md:inline") }) { Text("Dodaj stavku") }
                }
                Button({
                    classes("bg-emerald-600", "text-white", "px-4", "py-2", "md:px-6", "rounded-xl", "hover:bg-emerald-700", "transition-colors", "font-bold", "flex", "items-center", "gap-2")
                    if (isSaving) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick { handleSave() }
                }) {
                    if (isSaving) {
                        I({ classes("fas", "fa-spinner", "fa-spin") })
                    } else {
                        I({ classes("fas", "fa-save") })
                    }
                    Span({ classes("hidden", "md:inline") }) { Text("Spremi sve") }
                }
            }
        }

        if (isLoading) {
            Div({ classes("flex", "justify-center", "py-20") }) {
                I({ classes("fas", "fa-spinner", "fa-spin", "text-4xl", "text-emerald-500") })
            }
        } else if (plantings.isEmpty()) {
            Div({ classes("bg-white", "rounded-2xl", "border", "border-dashed", "border-gray-300", "p-12", "text-center") }) {
                I({ classes("fas", "fa-seedling", "text-4xl", "text-gray-300", "mb-4") })
                H3({ classes("text-xl", "font-medium", "text-gray-900", "mb-2") }) {
                    Text("Nema stavki za ovaj mjesec")
                }
                P({ classes("text-gray-500") }) {
                    Text("Kliknite na 'Dodaj stavku' da biste započeli kreiranje plana.")
                }
            }
        } else {
            Div({ classes("grid", "grid-cols-1", "gap-4") }) {
                plantings.forEach { item ->
                    PlantingItemCard(
                        item = item,
                        onEdit = {
                            editingItem = item
                            showForm = true
                        },
                        onDelete = { itemToDelete = item }
                    )
                }
            }
        }
    }

    if (showForm) {
        PlantingFormModal(
            item = editingItem,
            onDismiss = { showForm = false },
            showSnackbar = showSnackbar,
            onSave = { newItem ->
                if (editingItem != null) {
                    plantings = plantings.map { if (it.id == editingItem?.id) newItem else it }
                } else {
                    plantings = plantings + newItem
                }
                showForm = false
            }
        )
    }

    if (itemToDelete != null) {
        DeleteConfirmationModal(
            itemName = itemToDelete?.vegetableType ?: "",
            onDismiss = { itemToDelete = null },
            onConfirm = { handleDelete(itemToDelete!!) }
        )
    }

    if (showDeleteAllConfirm) {
        DeleteConfirmationModal(
            itemName = "sve stavke plana",
            onDismiss = { showDeleteAllConfirm = false },
            onConfirm = { handleDeleteAll() }
        )
    }
}

@Composable
private fun DeleteConfirmationModal(
    itemName: String,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit
) {
    Div({
        classes("fixed", "inset-0", "z-50", "flex", "items-center", "justify-center", "p-4", "bg-black", "bg-opacity-50")
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "w-full", "max-w-md", "overflow-hidden", "shadow-2xl")
            onClick { it.stopPropagation() }
        }) {
            Div({ classes("p-8", "text-center") }) {
                Div({ classes("w-20", "h-20", "bg-red-50", "rounded-full", "flex", "items-center", "justify-center", "mx-auto", "mb-6") }) {
                    I({ classes("fas", "fa-trash-alt", "text-3xl", "text-red-500") })
                }
                H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-2") }) {
                    Text("Brisanje stavke")
                }
                P({ classes("text-gray-500", "mb-8") }) {
                    Text("Da li ste sigurni da želite obrisati '$itemName'? Ova radnja se ne može poništiti.")
                }

                Div({ classes("flex", "gap-4") }) {
                    Button({
                        classes("flex-1", "px-6", "py-3", "rounded-xl", "bg-gray-100", "text-gray-700", "font-bold", "hover:bg-gray-200", "transition-colors")
                        onClick { onDismiss() }
                    }) {
                        Text("Odustani")
                    }
                    Button({
                        classes("flex-1", "px-6", "py-3", "rounded-xl", "bg-red-600", "text-white", "font-bold", "hover:bg-red-700", "transition-colors")
                        onClick { onConfirm() }
                    }) {
                        Text("Obriši")
                    }
                }
            }
        }
    }
}

@Composable
private fun PlantingItemCard(
    item: PlantingItem,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Div({
        classes("bg-white", "rounded-2xl", "border", "border-gray-100", "p-6", "shadow-sm", "hover:shadow-md", "transition-shadow", "group")
    }) {
        Div({ classes("flex", "justify-between", "items-start") }) {
            Div({ classes("flex", "items-center", "gap-4", "flex-1") }) {
                Div({ classes("flex-1") }) {
                    H3({ classes("text-lg", "font-bold", "text-gray-900", "mb-1") }) {
                        Text(item.vegetableType)
                    }
                    if (!item.varietySelection.isNullOrBlank()) {
                        P({ classes("text-emerald-600", "text-sm", "font-medium", "mb-3") }) {
                            Text(item.varietySelection)
                        }
                    }

                    Div({ classes("grid", "grid-cols-1", "sm:grid-cols-2", "gap-4", "mt-4") }) {
                        Div({ classes("flex", "items-center", "gap-3", "text-gray-500") }) {
                            I({ classes("fas", "fa-calendar-day", "w-5") })
                            Span { Text("Datum: ${item.sowingDate}") }
                        }
                        if (!item.recommendedSeedlingCount.isNullOrBlank()) {
                            Div({ classes("flex", "items-center", "gap-3", "text-gray-500") }) {
                                I({ classes("fas", "fa-sort-numeric-up", "w-5") })
                                Span { Text("Količina: ${item.recommendedSeedlingCount}") }
                            }
                        }
                    }

                    if (item.directPlanting) {
                        Div({ classes("mt-4", "inline-flex", "items-center", "gap-2", "bg-emerald-50", "text-emerald-700", "px-3", "py-1", "rounded-full", "text-xs", "font-bold") }) {
                            I({ classes("fas", "fa-check-circle") })
                            Text("Direktna sjetva")
                        }
                    }
                }

                Div({ classes("flex", "gap-2", "opacity-0", "group-hover:opacity-100", "transition-opacity") }) {
                    Button({
                        classes("p-2", "text-gray-400", "hover:text-blue-500", "transition-colors")
                        onClick { onEdit() }
                    }) {
                        I({ classes("fas", "fa-edit") })
                    }
                    Button({
                        classes("p-2", "text-gray-400", "hover:text-red-500", "transition-colors")
                        onClick { onDelete() }
                    }) {
                        I({ classes("fas", "fa-trash") })
                    }
                }
            }
        }
    }
}

@Composable
private fun PlantingFormModal(
    item: PlantingItem?,
    onDismiss: () -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit,
    onSave: (PlantingItem) -> Unit
) {
    var vegetableType by remember { mutableStateOf(item?.vegetableType ?: "") }
    var varietySelection by remember { mutableStateOf(item?.varietySelection ?: "") }
    var sowingDate by remember { mutableStateOf(item?.sowingDate ?: "") }
    var recommendedSeedlingCount by remember { mutableStateOf(item?.recommendedSeedlingCount ?: "") }
    var directPlanting by remember { mutableStateOf(item?.directPlanting ?: false) }

    Div({
        classes("fixed", "inset-0", "z-50", "flex", "items-center", "justify-center", "p-4", "bg-black", "bg-opacity-50")
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "w-full", "max-w-lg", "overflow-hidden", "shadow-2xl")
            onClick { it.stopPropagation() }
        }) {
            // Modal Header
            Div({ classes("bg-emerald-600", "p-6", "text-white", "flex", "justify-between", "items-center") }) {
                H2({ classes("text-xl", "font-bold") }) {
                    Text(if (item == null) "Nova stavka plana" else "Uredi stavku plana")
                }
                Button({
                    classes("text-white", "hover:rotate-90", "transition-transform")
                    onClick { onDismiss() }
                }) {
                    I({ classes("fas", "fa-times", "text-xl") })
                }
            }

            // Modal Body
            Div({ classes("p-8", "space-y-6") }) {
                // Vrsta povrća
                Div {
                    Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Vrsta povrća") }
                    Input(InputType.Text) {
                        classes("w-full", "px-4", "py-3", "rounded-xl", "border", "border-gray-200", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "outline-none", "transition-all")
                        placeholder("npr. Paradajz")
                        value(vegetableType)
                        onInput { vegetableType = it.value }
                    }
                }

                // Sorta
                Div {
                    Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Sorta (opcionalno)") }
                    Input(InputType.Text) {
                        classes("w-full", "px-4", "py-3", "rounded-xl", "border", "border-gray-200", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "outline-none", "transition-all")
                        placeholder("npr. Volovsko srce")
                        value(varietySelection)
                        onInput { varietySelection = it.value }
                    }
                }

                Div({ classes("grid", "grid-cols-2", "gap-4") }) {
                    // Datum sjetve
                    Div {
                        Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Datum sjetve") }
                        Input(InputType.Text) {
                            classes("w-full", "px-4", "py-3", "rounded-xl", "border", "border-gray-200", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "outline-none", "transition-all")
                            placeholder("npr. 15.03.")
                            value(sowingDate)
                            onInput { sowingDate = it.value }
                        }
                    }

                    // Količina
                    Div {
                        Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Količina") }
                        Input(InputType.Text) {
                            classes("w-full", "px-4", "py-3", "rounded-xl", "border", "border-gray-200", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "outline-none", "transition-all")
                            placeholder("npr. 50 kom")
                            value(recommendedSeedlingCount)
                            onInput { recommendedSeedlingCount = it.value }
                        }
                    }
                }

                // Direktna sjetva
                Div({ classes("flex", "items-center", "gap-3", "cursor-pointer") }) {
                    Input(InputType.Checkbox) {
                        id("direct-planting")
                        classes("w-5", "h-5", "rounded", "border-gray-300", "text-emerald-600", "focus:ring-emerald-500")
                        checked(directPlanting)
                        onInput { directPlanting = it.value }
                    }
                    Label(forId = "direct-planting", attrs = { classes("text-sm", "font-bold", "text-gray-700") }) {
                        Text("Direktna sjetva na otvorenom")
                    }
                }
            }

            // Modal Footer
            Div({ classes("p-8", "bg-gray-50", "flex", "gap-3") }) {
                Button({
                    classes("flex-1", "px-4", "py-3", "rounded-xl", "bg-white", "border", "border-gray-200", "text-gray-700", "font-bold", "hover:bg-gray-100", "transition-colors")
                    onClick { onDismiss() }
                }) { Text("Odustani") }
                Button({
                    classes("flex-1", "px-4", "py-3", "rounded-xl", "bg-emerald-600", "text-white", "font-bold", "hover:bg-emerald-700", "transition-colors")
                    onClick {
                        if (vegetableType.isBlank()) {
                            showSnackbar("Molimo unesite vrstu povrća.", SnackbarType.WARNING)
                            return@onClick
                        }
                        val newItem = (item ?: PlantingItem(
                            vegetableType = vegetableType,
                            sowingDate = sowingDate,
                            varietySelection = varietySelection,
                            recommendedSeedlingCount = recommendedSeedlingCount,
                            directPlanting = directPlanting
                        )).copy(
                            vegetableType = vegetableType,
                            varietySelection = varietySelection,
                            sowingDate = sowingDate,
                            recommendedSeedlingCount = recommendedSeedlingCount,
                            directPlanting = directPlanting
                        )
                        onSave(newItem)
                    }
                }) { Text("Potvrdi") }
            }
        }
    }
}
